import React from 'react';
import { useSelector } from 'react-redux';
import { GetServerSideProps } from 'next';
import _ from 'lodash';

import reduxWrapper from '@src/store';
import { getEvent, getEventState } from '@src/store/selectors/event';
import { getEventCall } from '@src/store/actions/event';
import { sendBugMsgCall } from '@src/store/actions/session';
import { setSSRPage } from '@src/store/actions/ssr';
import { defaultActionCalls } from '@src/router/ssr';
import { SSR_PAGE_EVENT } from '@src/router/hydrate';
import { getApiData, getPageTimeoutData } from '@src/utils/deprecatedCommon';
import { STATE_LOADING, STATE_FAIL, STATE_IDLE } from '@src/const/common';

import EventPage from '@src/components/event';
import CustomSeo from '@src/components/seo/customSeo';

const Event = (): JSX.Element => {
	const event = useSelector(getEvent);
	const seoSetting = {
		seoTitle: event?.seo_title || '',
		seoDescription: event?.seo_description || '',
		seoKeywords: event?.seo_keywords || '',
		seoImage: _.get(event, 'photos.0.link', ''),
	};

	return (
		<>
			<CustomSeo seoSetting={seoSetting} />
			<EventPage />
		</>
	);
};

export const getServerSideProps: GetServerSideProps = reduxWrapper.getServerSideProps(
	async ctx => {
		const { req, store, query } = ctx;
		const { ssrHeaders } = getApiData(req);
		const { eventShortLink } = query;
		const time = 3000;
		const pageTimeout = setTimeout(() => {
			const pageTimeoutData = getPageTimeoutData({ req, time });

			if (pageTimeoutData) {
				store.dispatch(sendBugMsgCall({ msg: JSON.stringify(pageTimeoutData) }));
			}
		}, time);

		store.dispatch(setSSRPage(SSR_PAGE_EVENT));

		const success = await new Promise<() => void>(resolve => {
			const unsubscribe = store.subscribe(() => {
				const state = store.getState();

				const defaultActionDone = defaultActionCalls.actionDone(ctx);
				const eventState = getEventState(state);
				const event = getEvent(state);

				if (defaultActionDone && (eventState === STATE_FAIL)) {
					// eslint-disable-next-line no-param-reassign
					ctx.res.statusCode = 404;
					clearTimeout(pageTimeout);
					resolve(unsubscribe);

					return;
				}

				if (event?.id && defaultActionDone && (eventState !== STATE_IDLE) && (eventState !== STATE_LOADING)) {
					clearTimeout(pageTimeout);
					resolve(unsubscribe);
				}
			});

			defaultActionCalls.actionCall(ctx);
			store.dispatch(getEventCall({ eventShortLink, ssrHeaders }));
		});

		success();
	},
);

export default Event;
